import "./loadDemanded.css";
import { useDispatch, useSelector } from "react-redux";
import { useState, useRef, useMemo, useEffect } from "react";
import { useConsultantVisibleContext } from "../../../features/consultant/screenContextAdapter";
import { useNavigate } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import MeasurementContext from "../../../context/MeasurementContext";
import DragDropCompanies from "../../../components/Measurement/DragDropCompanies";
import FrequencyComponent from "../../../components/FilterFrequency/FilterFrequency";
import ChartControls from "../../../components/Measurement/ChartControls";
import { useImageExport } from "../../../hooks/useImageExport";
import { getAllWidgets } from "../../../redux/apiCalls/widgetCall/widgetCall";
import { getLoadDemanded } from "../../../redux/apiCalls/analysisCall/loadDemandedCall";
import DemandedLoadChart from "../../../components/Charts/DemandedLoadChart.js";

const getInitialDates = () => {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return { start, end };
};

const formatNumber = (value, decimals = 2) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return num.toFixed(decimals);
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
};

const formatTableDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;

  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");

  return `${day}-${month}-${year} ${hours}:${minutes}`;
};

const InfoHint = ({ text }) => {
  return (
    <div className="info-hint">
      <button
        type="button"
        className="info-hint-button"
        aria-label="Más información"
      >
        i
      </button>
      <div className="info-hint-tooltip">{text}</div>
    </div>
  );
};

const LoadDemanded = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.currentUser);
  const userToken = user?.accessToken;
  const loadDemandedState = useSelector((state) => state.loadDemanded);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [firstSelect, setFirstSelect] = useState([]);
  const [meterSelected, setMeterSelected] = useState({
    tag: "loadDemanded",
    unit: "kW",
    name: "Carga Demandada",
  });
  const [showInput, setShowInput] = useState(false);
  const [buttonVisible, setButtonVisible] = useState(true);
  const [frec, setFrec] = useState("");
  const [availableBoards, setAvailableBoards] = useState([]);
  useConsultantVisibleContext({ firstSelect, startDate, endDate,
    selected_metric: meterSelected?.label || meterSelected?.tag, selected_unit: meterSelected?.unit });
  const graphRef = useRef(null);
  const exportNameRef = useRef(null);

  const { captureScreen, exportToPDF, handleImageExport } = useImageExport(
    firstSelect,
    meterSelected,
    startDate,
    endDate,
  );

  useEffect(() => {
    const { start, end } = getInitialDates();
    setStartDate(start);
    setEndDate(end);
  }, []);

  useEffect(() => {
    if (!userToken) return;

    getAllWidgets(dispatch, userToken).then((widgetsByBoard) => {
      const boards = Object.entries(widgetsByBoard)
        .filter(([_, widgets]) =>
          widgets.some((w) => w.template === "custom" && !w.config),
        )
        .map(([boardName, widgets]) => {
          const placeholder = widgets.find(
            (w) => w.template === "custom" && !w.config,
          );
          const realWidgets = widgets.filter(
            (w) => w.template === "custom" && w.config,
          );

          return {
            name: boardName,
            displayName: placeholder?.name || boardName,
            widgets: realWidgets,
          };
        });

      setAvailableBoards(boards);
    });
  }, [dispatch, userToken]);

  const selectedBranchId = useMemo(() => {
    if (!Array.isArray(firstSelect) || firstSelect.length === 0) return null;
    return firstSelect[0]?.id || null;
  }, [firstSelect]);

  useEffect(() => {
    if (!userToken || !selectedBranchId || !startDate || !endDate) return;

    getLoadDemanded(dispatch, userToken, selectedBranchId, startDate, endDate);
  }, [dispatch, userToken, selectedBranchId, startDate, endDate]);

  const handleValuesChange = (newFrec, newStartDate, newEndDate) => {
    setFrec(newFrec);
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const handleExportClick = () => {
    setShowInput((prev) => !prev);
    setButtonVisible(false);
  };

  const data = useMemo(() => {
    const raw = loadDemandedState?.loadDemanded;
    if (!raw) return null;

    if (typeof raw === "object") return raw;
    if (typeof raw === "string") {
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    }

    return null;
  }, [loadDemandedState?.loadDemanded]);

  const meta = data?.meta || null;
  const quality = data?.quality || null;
  const noData = Boolean(meta?.no_data);
  const topIntervals = data?.audit?.top_intervals || [];
  const contractedPower = data?.contracted_power || {};

  const chartData = useMemo(() => {
    return {
      baseLoad: 0,
      series: (data?.series || [])
        .filter((item) => Number(item.excess_kw || 0) > 0)
        .map((item) => ({
          timestamp: item.timestamp_utc || item.timestamp,
          total_energy: Number(item.excess_kw || 0),
        })),
    };
  }, [data]);

  const calculateResults = useMemo(() => {
    return {
      selected_bill_id: contractedPower?.selected_bill_id || null,
    };
  }, [contractedPower]);

  const contextValue = useMemo(
    () => ({ meterSelected, setMeterSelected }),
    [meterSelected],
  );

  const excessSeries = useMemo(() => {
    return (data?.series || []).filter(
      (item) => Number(item.excess_kw || 0) > 0,
    );
  }, [data]);

  return (
    <MeasurementContext.Provider value={contextValue}>
      <div className="container-load-demanded">
        <div className="page-content">
          <div className="cont-path">
            <p className="path path-cursor" onClick={() => navigate(`/`)}>
              <FormattedMessage id="Dashboard" defaultMessage="Dashboard" />
              {"> "}
            </p>
            <p className="path">
              <FormattedMessage
                id="LoadDemanded"
                defaultMessage="Carga Demandada"
              />
            </p>
          </div>

          <div className="measurement-load-demanded">
            <DragDropCompanies
              setFirstSelect={setFirstSelect}
              firstSelect={firstSelect}
              maxSelections={1}
            />

            <FrequencyComponent
              onValuesChange={handleValuesChange}
              startDate={startDate}
              endDate={endDate}
              isMaximeter={true}
            />

            <div id="graph" className="graph-load-demanded" ref={graphRef}>
              {noData ? (
                <div className="load-demanded-empty-state">
                  <div className="load-demanded-empty-state-box">
                    <p className="load-demanded-empty-title">
                      No se pudo calcular la carga demandada
                    </p>
                  </div>
                </div>
              ) : excessSeries.length === 0 ? (
                <div className="load-demanded-empty-state">
                  <div className="load-demanded-empty-state-box">
                    <p className="load-demanded-empty-title">
                      No se detectaron excesos
                    </p>
                    <p className="load-demanded-empty-message">
                      En el período seleccionado no hubo intervalos con demanda
                      superior a la potencia contratada.
                    </p>
                  </div>
                </div>
              ) : (
                <DemandedLoadChart data={chartData} />
              )}
            </div>

            <ChartControls
              onCaptureScreen={captureScreen}
              onExportPDF={() => exportToPDF(graphRef)}
              showExportInput={showInput}
              onExportClick={handleExportClick}
              onExportSubmit={() =>
                handleImageExport(graphRef, calculateResults)
              }
              buttonVisible={buttonVisible}
              nameInputRef={exportNameRef}
              hideCustomDashboard={true}
              hideChartTypes={true}
            />

            <div className="measurement-extra-panels">
              <div className="measurement-panel">
                <div className="measurement-panel-header">
                  <h3>Top excesos</h3>
                  <InfoHint text="Muestra los intervalos con mayor exceso detectado en el período seleccionado. Se compara la demanda medida contra la potencia contratada y se listan los casos más altos." />
                </div>
                <div className="measurement-panel-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Demanda</th>
                        <th>Contratada</th>
                        <th>Exceso</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topIntervals.length ? (
                        topIntervals.map((row, idx) => (
                          <tr key={`${row.timestamp_utc}-${idx}`}>
                            <td>
                              {formatTableDateTime(
                                row.timestamp_formatted || row.timestamp_utc,
                              )}
                            </td>
                            <td>{formatNumber(row.demand, 1)} kW</td>
                            <td>
                              {formatNumber(row.contracted_power_kw, 1)} kW
                            </td>
                            <td>{formatNumber(row.excess_kw, 1)} kW</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4">Sin excesos</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MeasurementContext.Provider>
  );
};

export default LoadDemanded;
